import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  TabStopPosition,
  TabStopType,
  UnderlineType,
} from "docx";

const FONT = "Times New Roman";
const SIZE_TITLE = 22; // 11pt
const SIZE_BODY = 18; // 9pt
const SIZE_SMALL = 16; // 8pt

const bold = (text: string, size = SIZE_BODY) =>
  new TextRun({ text, bold: true, font: FONT, size });

const normal = (text: string, size = SIZE_BODY) =>
  new TextRun({ text, font: FONT, size });

const underline = (text: string, size = SIZE_BODY) =>
  new TextRun({ text, font: FONT, size, underline: { type: UnderlineType.SINGLE } });

const SPACING = { before: 60, after: 60, line: 240 }; // tight
const SPACING_NONE = { before: 0, after: 0, line: 240 };
const SPACING_SECTION = { before: 120, after: 60, line: 240 };

export async function generateAmendmentWord() {
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: { top: 720, bottom: 720, left: 900, right: 900 }, // 0.5" top/bottom, 0.625" sides
          },
        },
        children: [
          // TITLE
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 40, line: 240 },
            children: [bold("AMENDMENT TO LAND INSTALLMENT CONTRACT", SIZE_TITLE)],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 100, line: 240 },
            children: [bold("(Assignment of Seller's Interest)", SIZE_BODY)],
          }),

          // Intro
          new Paragraph({
            spacing: SPACING,
            children: [
              normal("This Amendment (\"Amendment\") is made and entered into this ___ day of __________, 2025, by and between: "),
              bold("Jacob J.K. Byler and Lizzie Ann D. Byler"),
              normal(", husband and wife (\"Sellers\"); and "),
              bold("John Daniels and Callie Daniels"),
              normal(", husband and wife (\"Buyers\") (collectively, the \"Parties\")."),
            ],
          }),

          // RECITALS header
          new Paragraph({
            spacing: SPACING_SECTION,
            children: [bold("RECITALS")],
          }),

          new Paragraph({
            spacing: SPACING_NONE,
            children: [
              normal("WHEREAS, the Parties entered into a Land Installment Contract dated March 3, 2023, recorded in the Belmont County Recorder's Office as Instrument No. 202300001615, OR Volume 910, Pages 2263–2268 (\"Contract\"); and"),
            ],
          }),
          new Paragraph({
            spacing: SPACING_NONE,
            children: [
              normal("WHEREAS, §11 (Assignment) of the Contract currently prohibits the Sellers from assigning or transferring their interest; and"),
            ],
          }),
          new Paragraph({
            spacing: SPACING_NONE,
            children: [
              normal("WHEREAS, the Parties mutually desire to amend §11 to allow the Sellers to assign their interest, in whole or in part, including to a financial institution, lender, or subsequent purchaser; and"),
            ],
          }),
          new Paragraph({
            spacing: SPACING,
            children: [
              normal("WHEREAS, this Amendment does not modify any other term of the Contract, and does not change the Buyers' payment amount, schedule, rights, or obligations."),
            ],
          }),

          // AMENDMENT header
          new Paragraph({
            spacing: SPACING_SECTION,
            children: [bold("AMENDMENT — §11. ASSIGNMENT (Amended)")],
          }),

          new Paragraph({
            spacing: SPACING_NONE,
            children: [normal("Effective upon execution, §11 of the Land Installment Contract is hereby amended and restated as follows:")],
          }),

          // Bullet points as compact paragraphs
          ...[
            "Buyers shall not assign, encumber, or transfer Buyer's interest under this Contract without the prior written consent of Sellers.",
            "Sellers may assign, transfer, sell, or convey Seller's interest under this Contract, in whole or in part, without the consent of Buyers.",
            "Any such assignment shall not alter the Buyers' rights or obligations, and all payments made by Buyers after assignment shall be credited to the assignee as if paid to the Sellers.",
            "Buyers shall be provided written notice of any assignment and the address to which future payments shall be made.",
            "Any assignment by Sellers shall not relieve Sellers of obligations accrued prior to the date of assignment, unless expressly released in writing by the assignee.",
          ].map(
            (text) =>
              new Paragraph({
                spacing: SPACING_NONE,
                indent: { left: 360 },
                children: [normal("• " + text, SIZE_BODY)],
              })
          ),

          // No other changes
          new Paragraph({
            spacing: SPACING_SECTION,
            children: [
              bold("NO OTHER CHANGES. "),
              normal("Except as expressly amended herein, all other terms, conditions, rights, and obligations of the Land Installment Contract remain unchanged and in full force and effect."),
            ],
          }),

          // EXECUTION
          new Paragraph({
            spacing: SPACING_SECTION,
            children: [bold("EXECUTION")],
          }),
          new Paragraph({
            spacing: SPACING_NONE,
            children: [normal("IN WITNESS WHEREOF, the Parties have executed this Amendment on the date first written above.")],
          }),

          // Signature lines — two columns side by side using tabs
          new Paragraph({ spacing: { before: 140, after: 0, line: 240 }, children: [
            bold("SELLERS:"),
            new TextRun({ text: "\t", font: FONT, size: SIZE_BODY }),
            new TextRun({ text: "\t", font: FONT, size: SIZE_BODY }),
            new TextRun({ text: "\t", font: FONT, size: SIZE_BODY }),
            new TextRun({ text: "\t", font: FONT, size: SIZE_BODY }),
            new TextRun({ text: "\t", font: FONT, size: SIZE_BODY }),
            bold("BUYERS:"),
          ] }),

          new Paragraph({ spacing: { before: 200, after: 0, line: 240 }, children: [
            normal("_________________________________"),
            new TextRun({ text: "\t\t", font: FONT, size: SIZE_BODY }),
            normal("_________________________________"),
          ] }),
          new Paragraph({ spacing: SPACING_NONE, children: [
            normal("Jacob J.K. Byler", SIZE_SMALL),
            new TextRun({ text: "\t\t\t\t\t", font: FONT, size: SIZE_SMALL }),
            normal("John Daniels", SIZE_SMALL),
          ] }),

          new Paragraph({ spacing: { before: 160, after: 0, line: 240 }, children: [
            normal("_________________________________"),
            new TextRun({ text: "\t\t", font: FONT, size: SIZE_BODY }),
            normal("_________________________________"),
          ] }),
          new Paragraph({ spacing: SPACING_NONE, children: [
            normal("Lizzie Ann D. Byler", SIZE_SMALL),
            new TextRun({ text: "\t\t\t\t\t", font: FONT, size: SIZE_SMALL }),
            normal("Callie Daniels", SIZE_SMALL),
          ] }),

          // Notary - compact
          new Paragraph({
            spacing: { before: 180, after: 40, line: 240 },
            alignment: AlignmentType.CENTER,
            children: [bold("NOTARY ACKNOWLEDGMENT", SIZE_BODY)],
          }),
          new Paragraph({
            spacing: SPACING_NONE,
            children: [
              normal("STATE OF OHIO, COUNTY OF BELMONT, SS: On this ___ day of __________, 2025, before me, a Notary Public in and for said State, personally appeared Jacob J.K. Byler, Lizzie Ann D. Byler, John Daniels, and Callie Daniels, who acknowledged that they executed the foregoing Amendment as their free act and deed.", SIZE_SMALL),
            ],
          }),
          new Paragraph({ spacing: { before: 160, after: 0, line: 240 }, children: [
            normal("_________________________________", SIZE_SMALL),
          ] }),
          new Paragraph({ spacing: SPACING_NONE, children: [
            normal("Notary Public — My Commission Expires: __________", SIZE_SMALL),
          ] }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Amendment_to_Land_Installment_Contract.docx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
